<template>
  <div class="page-container area-page">
    <!-- 左侧区域树 -->
    <div class="left-tree-panel">
      <div class="panel-header">
        <span class="panel-title">区域结构</span>
        <el-button
          type="primary"
          size="mini"
          icon="el-icon-plus"
          @click="handleAddRoot"
        >
          新增区域
        </el-button>
      </div>
      <div
        v-loading="treeLoading"
        class="tree-wrapper"
      >
        <el-tree
          ref="areaTree"
          :data="treeData"
          :props="treeProps"
          node-key="uuid"
          highlight-current
          default-expand-all
          :expand-on-click-node="false"
          @node-click="handleNodeClick"
        >
          <template slot-scope="{ node, data }">
            <span class="tree-node-content">
              <span class="node-icon">{{ getNodeIcon(data.levelDepth) }}</span>
              <span class="node-label">{{ data.areaName }}</span>
              <span
                v-if="data.levelTypeName"
                class="node-tag"
                :class="'level-' + data.levelDepth"
              >{{ data.levelTypeName }}</span>
            </span>
          </template>
        </el-tree>
        <div
          v-if="!treeLoading && treeData.length === 0"
          class="empty-placeholder center"
        >
          <i class="el-icon-folder-opened" />
          <p>暂无区域数据</p>
        </div>
      </div>
    </div>

    <!-- 右侧详情面板 -->
    <div class="right-detail-panel">
      <!-- 区域详情卡片 -->
      <div
        v-if="currentNode"
        class="detail-card"
      >
        <div class="card-header">
          <span class="card-title">区域详情</span>
          <div class="header-actions">
            <el-button
              v-if="currentNode.levelDepth < 4"
              type="primary"
              size="mini"
              icon="el-icon-plus"
              @click="handleAddChild"
            >
              新增子区域
            </el-button>
            <el-button
              type="primary"
              plain
              size="mini"
              icon="el-icon-edit"
              @click="handleEdit"
            >
              编辑区域
            </el-button>
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete"
            >
              删除区域
            </el-button>
          </div>
        </div>
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">
              区域名称
            </div>
            <div class="info-value">
              {{ currentNode.areaName }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              区域标识
            </div>
            <div class="info-value">
              <code>{{ currentNode.areaCode || '-' }}</code>
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              层级深度
            </div>
            <div class="info-value">
              L{{ currentNode.levelDepth }} (第{{ currentNode.levelDepth }}层)
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              层级类型名称
            </div>
            <div class="info-value">
              <span
                class="level-tag"
                :class="'level-' + currentNode.levelDepth"
              >{{ currentNode.levelTypeName || '-' }}</span>
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              父级区域
            </div>
            <div class="info-value">
              {{ currentNode.parentName || '— (根节点)' }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              下级区域数
            </div>
            <div class="info-value">
              {{ (currentNode.children && currentNode.children.length) || 0 }} 个子区域
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              关联设备数
            </div>
            <div class="info-value device-count">
              {{ currentNode.deviceCount || 0 }}
            </div>
          </div>
          <div class="info-item full-width">
            <div class="info-label">
              说明
            </div>
            <div class="info-value">
              {{ currentNode.description || '-' }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              创建时间
            </div>
            <div class="info-value">
              {{ formatDateTime(currentNode.createTime) }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">
              更新时间
            </div>
            <div class="info-value">
              {{ formatDateTime(currentNode.updateTime) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 未选中时显示提示 -->
      <div
        v-if="!currentNode"
        class="empty-placeholder center"
      >
        <i class="el-icon-mouse" />
        <p>请在左侧选择区域查看详情</p>
      </div>

      <!-- 层级说明卡片 -->
      <div class="level-hint-card">
        <div class="card-header">
          <span class="card-title">层级说明</span>
        </div>
        <el-table
          :data="levelHintData"
          border
          size="small"
        >
          <el-table-column
            prop="depth"
            label="层级深度"
            width="100"
          >
            <template slot-scope="scope">
              <span
                class="level-tag"
                :class="'level-' + scope.row.depth"
              >L{{ scope.row.depth }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="types"
            label="常用类型名称"
            width="200"
          />
          <el-table-column
            prop="desc"
            label="说明"
          />
        </el-table>
        <div class="hint-text">
          层级类型名称可自由定义，不限于上述示例。层级深度(L1-L4)由系统根据父子关系自动计算。
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <AreaDialog
      :visible.sync="dialogVisible"
      :type="dialogType"
      :node-data="currentNode"
      :tree-data="treeData"
      :initial-parent-id="initialParentId"
      @success="handleDialogSuccess"
    />
  </div>
</template>

<script>
// 服务
import { areaService } from './area.service';
// 工具函数
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
// 组件
import AreaDialog from './areaDialog.vue';

export default {
  name: 'AreaList',
  components: {
    AreaDialog
  },
  data () {
    return {
      treeData: [],
      treeLoading: false,
      treeProps: {
        children: 'children',
        label: 'areaName'
      },
      currentNode: null,
      dialogVisible: false,
      dialogType: 'add',
      initialParentId: '',
      // 层级说明数据
      levelHintData: [
        { depth: 1, types: '园区、大区、项目', desc: '最顶层区域，如：广交会A区、XX产业园' },
        { depth: 2, types: '场馆、楼栋、单元、室外区域', desc: '第二层区域，可以是建筑物或室外公共区' },
        { depth: 3, types: '楼层、通道、广场、出入口', desc: '第三层区域，楼层或室外的细分区域' },
        { depth: 4, types: '功能区、房间、展区、设备区', desc: '最细粒度区域，设备的实际安装位置' }
      ],
      // 层级图标
      levelIconMap: {
        1: '🏢',
        2: '🏗️',
        3: '📐',
        4: '📍'
      }
    };
  },
  methods: {
    initData () {
      this.loadTreeData();
    },

    loadTreeData () {
      this.treeLoading = true;
      areaService.getAreaTree().then(res => {
        this.treeData = res.data || [];
      }).catch(err => {
        console.error(err);
      }).finally(() => {
        this.treeLoading = false;
      });
    },

    getNodeIcon (level) {
      return this.levelIconMap[level] || '📍';
    },

    handleNodeClick (data) {
      this.currentNode = data;
    },

    handleAddRoot () {
      this.dialogType = 'add';
      this.initialParentId = '';
      this.dialogVisible = true;
    },

    handleAddChild () {
      if (!this.currentNode) return;
      if (this.currentNode.levelDepth >= 4) {
        this.$message.warning('已达到最大层级深度(L4)，无法继续添加子区域');
        return;
      }
      this.dialogType = 'addChild';
      this.initialParentId = this.currentNode.uuid;
      this.dialogVisible = true;
    },

    handleEdit () {
      if (!this.currentNode) return;
      this.dialogType = 'edit';
      this.dialogVisible = true;
    },

    handleDelete () {
      if (!this.currentNode) return;
      if (this.currentNode.children && this.currentNode.children.length > 0) {
        this.$message.warning('该区域存在子区域，请先删除子区域');
        return;
      }
      if (this.currentNode.deviceCount > 0) {
        this.$message.warning('该区域存在关联设备，请先解除设备关联');
        return;
      }
      this.$confirm(`确认删除区域"${this.currentNode.areaName}"吗？`, '提示', {
        type: 'warning'
      }).then(() => {
        this.treeLoading = true;
        areaService.deleteArea(this.currentNode.uuid).then(() => {
          this.$message.success('删除成功');
          this.currentNode = null;
          this.loadTreeData();
        }).finally(() => {
          this.treeLoading = false;
        });
      }).catch(() => {});
    },

    handleDialogSuccess () {
      this.loadTreeData();
      if (this.dialogType === 'edit') {
        this.currentNode = null;
      }
    },

    formatDateTime
  },
  mounted () {
    this.initData();
  }
};
</script>

<style scoped lang="scss">
.area-page {
  display: flex;
  height: calc(100vh - 140px); /* 注意：如果你的导航栏+顶栏高度大于84px，这里可能要调大，比如 110px */
  background: #f5f7fa;
  padding: 15px;
  gap: 15px;
  box-sizing: border-box;
  overflow: hidden; /* 🌟 必须：锁死最外层，绝不让页面整体滚动 */

  // 左侧树面板
  .left-tree-panel {
    width: 450px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    height: 100%; /* 🌟 必须：高度拉满父级，但不超出 */

    .panel-header {
      padding: 15px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0; /* 🌟 必须：保证头部不被挤压变形 */

      .panel-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    .tree-wrapper {
      flex: 1;
      height: 0; /* 🌟 核弹级属性：强制基础高度为0，彻底解决内容撑开外层的问题 */
      overflow-y: auto; /* 内部滚动 */
      padding: 15px;

      .tree-node-content {
        display: flex;
        align-items: center;
        flex: 1;

        .node-icon {
          margin-right: 8px;
          font-size: 16px;
        }

        .node-label {
          margin-right: 10px;
          flex: 1;
        }

        .node-tag {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          margin-left: auto;

          &.level-1 { background: #ecf5ff; color: #2296f7; }
          &.level-2 { background: #f0f9eb; color: #67c23a; }
          &.level-3 { background: #fdf6ec; color: #e6a23c; }
          &.level-4 { background: #f4ecfb; color: #a27ce3; }
        }
      }
    }
  }

  // 右侧详情面板
  .right-detail-panel {
    flex: 1;
    height: 100%; /* 🌟 必须：固定高度 */
    overflow-y: auto; /* 🌟 必须：允许右侧内容独立滚动 */
    background: transparent;
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: 15px;

    /* 为了防止右侧 flex 子元素也被撑爆，加上这段 */
    & > * {
      flex-shrink: 0;
    }

    // 详情卡片
    .detail-card {
      background: #fff;
      border-radius: 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

      .card-header {
        padding: 15px;
        border-bottom: 1px solid #ebeef5;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .card-title {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }
      }

      .info-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        padding: 20px;
        gap: 0;

        .info-item {
          padding: 12px 15px;
          border-bottom: 1px solid #ebeef5;
          border-right: 1px solid #ebeef5;

          &:nth-child(2n) {
            border-right: none;
          }

          &.full-width {
            grid-column: span 2;
            border-right: none;
          }

          .info-label {
            font-size: 12px;
            color: #909399;
            margin-bottom: 6px;
          }

          .info-value {
            font-size: 14px;
            color: #303133;

            code {
              color: #2296f7;
              font-family: monospace;
              font-size: 12px;
            }

            &.tree-code {
              font-family: monospace;
              color: #2296f7;
            }

            &.device-count {
              color: #2296f7;
              font-size: 24px;
              font-weight: 600;
            }
          }
        }
      }
    }

    // 层级说明卡片
    .level-hint-card {
      background: #fff;
      border-radius: 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

      .card-header {
        padding: 15px;
        border-bottom: 1px solid #ebeef5;

        .card-title {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
        }
      }

      .el-table {
        margin: 15px;
        width: calc(100% - 30px);
      }

      .hint-text {
        margin: 0 15px 15px;
        padding: 10px 15px;
        background: #f5f7fa;
        border-radius: 4px;
        font-size: 12px;
        color: #909399;
        line-height: 1.5;
      }
    }
  }

  // 层级标签样式
  .level-tag {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;

    &.level-1 { background: #ecf5ff; color: #2296f7; }
    &.level-2 { background: #f0f9eb; color: #67c23a; }
    &.level-3 { background: #fdf6ec; color: #e6a23c; }
    &.level-4 { background: #f4ecfb; color: #a27ce3; }
  }

  .form-hint {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
    line-height: 1.5;
  }

  .empty-placeholder {
    padding: 40px 0;
    text-align: center;
    color: #909399;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    i {
      font-size: 48px;
      margin-bottom: 10px;
      color: #E4E7ED;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}
</style>

<style>
::v-deep .el-dialog {
  .el-form-item {
    margin-bottom: 15px;
  }
  .el-form-item__error {
    position: relative;
    top: 0;
    padding-top: 2px;
  }
}

::v-deep .el-tree-node__content {
  height: 36px;
  padding-right: 10px;
}

::v-deep .el-tree-node__content:hover {
  background-color: #f5f7fa;
}

::v-deep .el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content {
  background-color: #ecf5ff;
}

/* 锁定L1和L2层级不允许收缩（隐藏展开图标并禁用点击） */
::v-deep .el-tree > .el-tree-node > .el-tree-node__content > .el-tree-node__expand-icon,
::v-deep .el-tree > .el-tree-node > .el-tree-node__children > .el-tree-node > .el-tree-node__content > .el-tree-node__expand-icon {
  opacity: 0;
  pointer-events: none;
}
</style>

<!-- 全局样式：确保消息提示和确认框不被弹窗遮挡 -->
<style lang="scss">
.el-message {
  z-index: 10000 !important;
}
.el-message-box__wrapper {
  z-index: 10001 !important;
}
</style>
